import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { recordingsAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { Modal } from '../../ui';

/**
 * Confirms it's you before a download or deletion: a code from your authenticator app, or one emailed now. Good for a
 * few minutes in this sign-in; then onVerified runs the action that asked for it.
 */
export default function StepUpModal({ action, onVerified, onClose }) {
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const start = useCallback(() => {
    setError('');
    setCode('');
    recordingsAPI.stepUp().then((res) => setChallenge(res.data)).catch((err) => setError(apiError(err, 'Could not start verification')));
  }, []);
  useEffect(() => { start(); }, [start]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await recordingsAPI.verifyStepUp(challenge.challenge_id, code.trim());
      onVerified();
    } catch (err) {
      setError(apiError(err, 'That code isn\'t right.'));
      if (err.response?.data?.detail?.code === 'step_up_expired') setChallenge(null);
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Confirm it's you" onClose={onClose}>
      <form className="rc-stepup" onSubmit={submit}>
        <p><ShieldCheck size={16} /> To {action} recordings, enter a verification code. It stays valid for a few minutes on this device.</p>
        {!challenge && !error && <p className="cell-muted">Preparing…</p>}
        {challenge && (
          <p className="cell-muted">{challenge.method === 'totp'
            ? 'Open your authenticator app and enter its 6-digit code (or a backup code).'
            : `We sent a 6-digit code to ${challenge.sent_to}. It expires in ${challenge.minutes} minutes.`}</p>
        )}
        <input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={20} value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="123456" aria-label="Verification code" disabled={!challenge} />
        {error && <p className="rc-error" role="alert">{error}</p>}
        <div className="rc-stepup-actions">
          {challenge?.method === 'email' && <button type="button" className="secondary-action" onClick={start} disabled={busy}>Send a new code</button>}
          {!challenge && error && <button type="button" className="secondary-action" onClick={start}>Try again</button>}
          <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-action" disabled={busy || !challenge || code.trim().length < 4}>{busy ? 'Checking…' : 'Verify'}</button>
        </div>
      </form>
    </Modal>
  );
}
